import { useEffect, useState } from 'react';
import { ButtonBase } from '@mui/material';

import { useTRPCClient } from '#src/trpc.js';

interface SourceEntry {
	fileId: string;
	pageNumbers: number[];
}

interface ResolvedSource extends SourceEntry {
	url: string | null;
}

export interface SourcesMarkdownItemProps {
	/** Raw JSON string encoding an array of {@link SourceEntry} objects. */
	jsonData?: string;
}

/**
 * Renders clickable page-number chips (or a generic "view document" chip) linking
 * to the referenced datasheet pages.
 *
 * Receives a JSON array of `{ fileId, pageNumbers }` entries. For each entry,
 * resolves the file URL via `file.getUrl`, then renders one chip
 * per page number that opens `url#page=N` in a new tab. A chip whose URL cannot be
 * resolved is rendered disabled. When no page numbers are available (documents
 * indexed before the page annotation was added), a single "view document" chip is
 * shown that opens the file root.
 *
 * @param jsonData - Raw JSON string from the `:::sources` directive body.
 * @returns The source chips (a chip is disabled when its URL can't be resolved), or
 *   null while loading or when there are no sources.
 */
export function SourcesMarkdownItem({ jsonData }: SourcesMarkdownItemProps) {
	const trpcClient = useTRPCClient();
	const [sources, setSources] = useState<ResolvedSource[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (!jsonData) {
			setLoading(false);
			return;
		}

		// Re-enter the loading state so stale chips from a previous payload aren't
		// shown while the new URLs resolve.
		setLoading(true);

		let cancelled = false;

		async function resolve(): Promise<void> {
			let entries: SourceEntry[];
			try {
				const raw = JSON.parse(jsonData ?? '') as unknown;
				if (!Array.isArray(raw)) {
					setLoading(false);
					return;
				}
				// Normalize each element: skip non-objects and coerce pageNumbers to a
				// valid number array so malformed entries like `[{}]` don't crash renders.
				entries = raw
					.filter((item): item is Record<string, unknown> => item !== null && typeof item === 'object')
					.map((item) => ({
						fileId: typeof item['fileId'] === 'string' ? item['fileId'] : '',
						pageNumbers:
							Array.isArray(item['pageNumbers']) ?
								(item['pageNumbers'] as unknown[]).filter((p): p is number => typeof p === 'number')
							:	[],
					}))
					.filter((entry) => entry.fileId !== '');
			} catch {
				setLoading(false);
				return;
			}

			const resolved = await Promise.all(
				entries.map(async (entry) => {
					try {
						const { url } = await trpcClient.file.getUrl.query({ fileId: entry.fileId });
						return { ...entry, url };
					} catch {
						return { ...entry, url: null };
					}
				}),
			);

			if (!cancelled) {
				setSources(resolved);
				setLoading(false);
			}
		}

		void resolve();
		return () => {
			cancelled = true;
		};
	}, [jsonData, trpcClient]);

	if (loading || sources.length === 0) return null;

	return (
		<div className='mt-3 flex flex-wrap items-center gap-2 border-t border-gray-a4 pt-2.5'>
			<span className='font-medium text-gray-a9'>Sources</span>
			{sources.flatMap((source) => {
				if (source.pageNumbers.length > 0) {
					return source.pageNumbers.map((pageNumber) => (
						<SourceChip
							key={`${source.fileId}-${pageNumber}`}
							label={`page ${pageNumber}`}
							url={source.url ? `${source.url}#page=${pageNumber}` : null}
						/>
					));
				}
				// No page numbers for this source — offer a generic document link.
				return [<SourceChip key={source.fileId} label='view document' url={source.url} />];
			})}
		</div>
	);
}

interface SourceChipProps {
	label: string;
	url: string | null;
}

function SourceChip({ label, url }: SourceChipProps) {
	return (
		<ButtonBase
			disabled={!url}
			onClick={() => url && window.open(url, '_blank', 'noopener,noreferrer')}
			className='rounded-full border border-gray-a5 bg-gray-a2 px-2.5 py-0.5 text-gray-11 transition-colors hover:border-blue-8 hover:bg-blue-a2 hover:text-blue-11 disabled:cursor-default disabled:opacity-50'
		>
			{label}
		</ButtonBase>
	);
}
